/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { transformToRequest } from '@features/property-management/components/PropertyFormDualPane/utils';
import type { PropertyFormValues } from '@features/property-management/components/PropertyFormDualPane/schema';
import {
  ListingsCopyService,
  type ListingSuggestionKind,
} from '@services/requests/housien-services/ListingsCopyService';

/**
 * Fetch listing copy suggestions for a property from the backend suggestions
 * service. The deterministic template engine now lives server-side; this
 * wrapper nests flat form values into the VO shape the engine reads
 * (`classification`, `location`, `characteristics`, …) and unwraps
 * `suggestions` from the response.
 *
 * The agency is sent in the request body (not the URL): the backend evaluates
 * the caller's `view_property` permission against it.
 *
 * @param agencyId - The agency that owns the property.
 * @param kind - Whether to suggest a `title` or a `description`.
 * @param propertyValues - Flat property form field values (`form.getValues()`).
 * @param count - How many distinct suggestions to request (default 5).
 * @returns A promise resolving to the list of suggestion strings.
 */
export async function getListingSuggestions(
  agencyId: string,
  kind: ListingSuggestionKind,
  propertyValues: Record<string, unknown>,
  count = 5,
): Promise<string[]> {
  const property = transformToRequest(propertyValues as PropertyFormValues);
  const result = await ListingsCopyService.suggestListingCopy({
    agency_id: agencyId,
    kind,
    property: property as unknown as Record<string, unknown>,
    count,
  });
  return result.suggestions;
}
